import api from "../../api";
import type {
  ContentDetailsApiItem,
  ContentDetailsRequestConfig,
} from "../../../@types/portalAluno";
import { asId, asObject } from "../../../utils/portalAluno/coerce";

const getRawCourse = async (idCurso: number) => {
  const response = await api.get<ContentDetailsApiItem>("mobile/curso", {
    params: { idCurso },
  });

  return response.data;
};

const getRawContent = async (idConteudo: number) => {
  const response = await api.get<ContentDetailsApiItem>("mobile/conteudo", {
    params: { idConteudo },
  });

  return response.data;
};

const hasContentTypeMetadata = (item: ContentDetailsApiItem) => {
  const nestedTypeName = asObject<ContentDetailsApiItem>(item.conteudoTipo)
    ?.nmConteudoTipo;

  return Boolean(
    item.pathConteudo ||
      item.urlExternaConteudo ||
      item.nmConteudoTipo ||
      nestedTypeName
  );
};

const relationFields = [
  "isRealizado",
  "ordemConteudo",
  "vlConteudoOrdem",
] as const;

const mergeFullContent = (
  summary: ContentDetailsApiItem,
  fullContent: ContentDetailsApiItem
) => {
  const merged = { ...summary, ...fullContent };

  relationFields.forEach((field) => {
    if (summary[field] !== undefined) merged[field] = summary[field];
  });

  return merged;
};

const hydrateContentCollection = async (
  value: unknown,
  contentRequests: Map<number, Promise<ContentDetailsApiItem>>
) => {
  if (!Array.isArray(value)) return value;

  return Promise.all(
    value.map(async (candidate) => {
      const item = asObject<ContentDetailsApiItem>(candidate);
      if (!item) return candidate;
      if (hasContentTypeMetadata(item)) return item;

      const idConteudo = asId(item.idConteudo);
      if (!idConteudo) return item;

      let request = contentRequests.get(idConteudo);
      if (!request) {
        request = getRawContent(idConteudo);
        contentRequests.set(idConteudo, request);
      }

      try {
        return mergeFullContent(item, await request);
      } catch {
        // O item resumido ainda deve aparecer se uma API legada não permitir
        // buscar seus metadados completos.
        return item;
      }
    })
  );
};

export const hydrateEntityContents = async (
  raw: ContentDetailsApiItem,
  config: ContentDetailsRequestConfig
) => {
  if (!config.childKey) return raw;
  const contentRequests = new Map<number, Promise<ContentDetailsApiItem>>();

  if (config.entityType === "trilha") {
    const rawCourses = raw[config.childKey];
    if (!Array.isArray(rawCourses)) return raw;

    const courses = await Promise.all(
      rawCourses.map(async (candidate: unknown) => {
        const course = asObject<ContentDetailsApiItem>(candidate);
        if (!course) return candidate;

        return {
          ...course,
          cursoConteudos: await hydrateContentCollection(
            course.cursoConteudos,
            contentRequests
          ),
        };
      })
    );

    return { ...raw, [config.childKey]: courses };
  }

  return {
    ...raw,
    [config.childKey]: await hydrateContentCollection(
      raw[config.childKey],
      contentRequests
    ),
  };
};

export const hydrateTrailCourses = async (raw: ContentDetailsApiItem) => {
  if (!Array.isArray(raw.trilhaCursos)) return raw;

  const trilhaCursos = await Promise.all(
    raw.trilhaCursos.map(async (candidate) => {
      const course = asObject<ContentDetailsApiItem>(candidate);
      if (!course) return candidate;

      const idCurso = asId(course.idCurso);
      if (!idCurso) return course;

      try {
        return await getRawCourse(idCurso);
      } catch {
        // A trilha continua utilizável mesmo que um curso específico não possa
        // ser enriquecido com seus conteúdos.
        return course;
      }
    })
  );

  return { ...raw, trilhaCursos };
};
